import React, {
  useMemo,
  useState,
} from 'react';
import {
  ColumnHeader,
} from 'dtos/columnHeader';
import {
  asArray,
  buildTableRows,
} from 'views/EMR/components/Eligibility/utils';
import isNil from 'lodash/isNil';
import {
  ServiceTypeBreakdown,
} from 'dtos';
import {
  EligibilityCell,
} from 'views/EMR/components/Eligibility/EligibilityCell';

type EligibilityData = {
  columnHeaders: readonly ColumnHeader[] | ColumnHeader[];
  serviceTypeBreakdowns?: readonly ServiceTypeBreakdown[] | ServiceTypeBreakdown[];
};

export type Props = EligibilityData & {
  emptyStateText?: string;
};

type BreakdownTableProps = {
  breakdown: ServiceTypeBreakdown;
  columnHeaders: readonly ColumnHeader[] | ColumnHeader[];
};

function BreakdownTable({
  breakdown,
  columnHeaders,
}: BreakdownTableProps) {
  const rows = useMemo(
    () => (isNil(breakdown)
      ? []
      : buildTableRows(
        asArray<ServiceTypeBreakdown>(breakdown?.benefitType),
        [...columnHeaders],
      )),
    [breakdown, columnHeaders],
  );

  const [hoveredGroup, setHoveredGroup] = useState<string | null>(null);

  return (
    <table className="min-w-full text-sm border-collapse">
      <thead className="sticky top-10 z-10 text-xs text-gray-800">
        <tr>
          {columnHeaders.map((header) => (
            <th key={header.name} className="px-2 py-2 border-x border-slate-200 text-left whitespace-nowrap bg-gray-100 font-semibold">
              {header.name}
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {rows.map((row) => (
          <tr
            key={row.groupId}
            onMouseEnter={() => setHoveredGroup(row.hoverGroupId)}
            onMouseLeave={() => setHoveredGroup(null)}
            className={hoveredGroup === row.hoverGroupId ? 'bg-blue-50' : ''}
          >
            {columnHeaders.map((header, depth) => {
              const cell = row[`col_${depth}`];

              return (isNil(cell) || cell?.rowSpan <= 0) ? null : (
                <td key={`cell-${row.groupId}-${header.name ?? header.key}`} rowSpan={cell.rowSpan} className="text-xs px-2 py-2 border border-gray-200 align-top">
                  <EligibilityCell value={cell.value} header={header} />
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function EligibilityInformation({
  columnHeaders,
  serviceTypeBreakdowns,
  emptyStateText = 'No eligibility information to display.',
}: Props) {
  const breakdowns = serviceTypeBreakdowns ?? [];

  const showEmpty = breakdowns.length === 0;

  return (
    <div className="w-full min-h-0 flex-1 flex flex-col pb-4 overflow-auto">
      {showEmpty ? (
        <div className="bg-white border border-gray-200 rounded-xl p-6 text-base font-medium text-gray-700">{emptyStateText}</div>
      ) : (
        <div className="flex flex-col gap-6 px-4">
          {breakdowns?.map((breakdown) => (
            <div
              key={breakdown?.serviceTypeCode}
              className="flex flex-col gap-2"
            >
              <div className="sticky top-0 z-20 bg-white h-10 flex items-center text-sm font-semibold text-gray-700">
                STC
                {' '}
                {breakdown?.serviceTypeCode}
                {' '}
                -
                {' '}
                {breakdown?.serviceType}
              </div>
              <div className="rounded-xl bg-white shadow">
                <BreakdownTable breakdown={breakdown} columnHeaders={columnHeaders} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
